import { formatDate, sortAsc } from '@strata/helper-functions';
import dayjs from 'dayjs';

import { httpDelete, patch, put } from 'services/Http';

export const TherapyGroupPreferencesApiUrl = '/reference/therapy_group_preferred_products';

export const therapyGroupPreferencesUrl = (therapyGroupName) =>
    `${TherapyGroupPreferencesApiUrl}/${therapyGroupName}`;

export const therapyGroupPreferenceWindowsUrl = (therapyGroupName) =>
    `${therapyGroupPreferencesUrl(therapyGroupName)}/windows`;

export const therapyGroupGlobalPreferenceUrl = (therapyGroupName) =>
    `${therapyGroupPreferencesUrl(therapyGroupName)}/global`;

export const therapyGroupSegmentPreferenceUrl = (therapyGroupName, segmentId) =>
    `${therapyGroupPreferencesUrl(therapyGroupName)}/segments/${segmentId}`;

export const preferenceWindowUrl = (preferenceId) =>
    `${TherapyGroupPreferencesApiUrl}/${preferenceId}`;

export const EMPTY_PREFERENCES = { global: null, segments: {} };

// The summary lists the windows in effect at its as_of; a null segment_id is the global default.
export const preferencesFromResponse = (response) => {
    const current = response?.preferences ?? [];
    return {
        global: current.find((pref) => !pref.segment_id)?.product_service_oid ?? null,
        segments: Object.fromEntries(
            current
                .filter((pref) => pref.segment_id)
                .map((pref) => [pref.segment_id, pref.product_service_oid]),
        ),
    };
};

const windowFromTarget = (target) => ({
    preferenceId: target.preference_id,
    segmentId: target.segment_id ?? null,
    oid: target.product_service_oid ?? null,
    start: target.effective_start,
    end: target.effective_end ?? null,
});

const compareWindows = (a, b) =>
    sortAsc(a.segmentId ?? '', b.segmentId ?? '') || dayjs(a.start).diff(b.start);

// Every window on the response, global first, then by segment and start. A segmentId of
// null is the global default.
export const preferenceWindowsFromResponse = (response) =>
    (response?.preferences ?? []).map(windowFromTarget).sort(compareWindows);

// Half-open [start, end), the same rule as the point-in-time preference query.
export const windowInEffect = (window, day) => {
    const asOf = dayjs(day);
    const started = !asOf.isBefore(dayjs(window.start));
    const stillOpen = !window.end || asOf.isBefore(dayjs(window.end));
    return started && stillOpen;
};

export const windowsInEffect = (windows, day) =>
    (windows ?? []).filter((window) => windowInEffect(window, day));

export const preferencesFromWindows = (windows) =>
    preferencesFromResponse({
        preferences: windows.map((window) => ({
            segment_id: window.segmentId,
            product_service_oid: window.oid,
        })),
    });

// Calendar dates in the UI mean the start of that day in the user's time zone, the same
// convention as the other Lucent date dialogs (dayjs(...).toISOString()).
export const windowStartFromDay = (day) => dayjs(day).startOf('day').toISOString();

// The UI shows the last day a window applies (inclusive); the API stores the instant it stops
// applying (exclusive), which is the start of the following day. No day means open-ended.
export const windowEndFromLastDay = (lastDay) =>
    lastDay ? dayjs(lastDay).add(1, 'day').startOf('day').toISOString() : null;

export const firstDayOfWindow = (start) => dayjs(start).startOf('day').toDate();

// An end that is not at midnight (a replace closes a window mid-day) still counts that day.
export const lastDayOfWindow = (end) =>
    end ? dayjs(end).subtract(1, 'millisecond').startOf('day').toDate() : null;

// An open-ended window has no stored end; this is only how one is displayed.
export const OPEN_END_LABEL = '12/31/2199';

// The days a window applies, start to inclusive end.
export const windowTooltip = (window) => {
    const end = window.end ? formatDate(lastDayOfWindow(window.end)) : OPEN_END_LABEL;
    return `${formatDate(firstDayOfWindow(window.start))} - ${end}`;
};

// What the Client Preference cell lists for one OID row: the targets using it (from the
// possibly edited preferences), each with its loaded window when there is one. A segmentId of
// null is the global default.
export const clientPreferenceEntries = ({ oid, isGlobalDefault, segmentIds }, windows) =>
    [...(isGlobalDefault ? [null] : []), ...segmentIds].map((segmentId) => ({
        segmentId,
        window: windows.find((window) => window.oid === oid && window.segmentId === segmentId),
    }));

const findParent = (parentProducts, parentProductService) =>
    (parentProducts ?? []).find((pps) => pps.parent_product_service === parentProductService);

export const oidsForParent = (parentProducts, parentProductService) =>
    findParent(parentProducts, parentProductService)?.oids ?? [];

export const productDescriptionFor = (parentProducts, oid) =>
    (parentProducts ?? [])
        .flatMap((pps) => pps.product_details ?? [])
        .find((detail) => detail.oid === oid)?.description ?? oid;

const segmentIdsFor = (preferences, oid) =>
    Object.entries(preferences.segments)
        .filter(([, segmentOid]) => segmentOid === oid)
        .map(([segmentId]) => segmentId)
        .sort(sortAsc);

const oidRow = ({ parent, oid, index, parentProducts, preferences }) => {
    const isGlobalDefault = preferences.global === oid;
    const segmentIds = segmentIdsFor(preferences, oid);
    return {
        id: oid,
        parentProductService: parent?.parent_product_service ?? null,
        parentDisplayName: parent?.display_name ?? '',
        isFirstOfParent: index === 0,
        oid,
        description: productDescriptionFor(parentProducts, oid),
        isGlobalDefault,
        segmentIds,
        clientPreference: [...(isGlobalDefault ? ['Global default'] : []), ...segmentIds].join(
            ', ',
        ),
    };
};

const unlistedPreferenceOids = (preferences, listedOids) =>
    [...new Set([preferences.global, ...Object.values(preferences.segments)])]
        .filter((oid) => oid && !listedOids.has(oid))
        .sort(sortAsc);

export const getTherapyGroupOidRows = ({ parentNames, parentProducts, preferences }) => {
    const listed = parentNames
        .map((name) => findParent(parentProducts, name))
        .filter(Boolean)
        .sort((a, b) => sortAsc(a.display_name, b.display_name))
        .flatMap((parent) =>
            [...parent.oids]
                .sort(sortAsc)
                .map((oid, index) => oidRow({ parent, oid, index, parentProducts, preferences })),
        );
    const listedOids = new Set(listed.map((row) => row.oid));
    const unlisted = unlistedPreferenceOids(preferences, listedOids).map((oid) =>
        oidRow({ parent: null, oid, index: 0, parentProducts, preferences }),
    );
    return [...listed, ...unlisted];
};

export const applyClientPreference = (preferences, { oid, isGlobalDefault, segmentIds }) => {
    const selected = new Set(segmentIds);
    const segments = Object.fromEntries(
        Object.entries(preferences.segments).filter(
            ([segmentId, segmentOid]) => segmentOid !== oid && !selected.has(segmentId),
        ),
    );
    segmentIds.forEach((segmentId) => {
        segments[segmentId] = oid;
    });
    return { global: isGlobalDefault ? oid : preferences.global, segments };
};

export const canRemoveParent = (preferences, oids) => !oids.includes(preferences.global);

export const removeOidsFromPreferences = (preferences, oids) => {
    const removed = new Set(oids);
    return {
        ...preferences,
        segments: Object.fromEntries(
            Object.entries(preferences.segments).filter(([, oid]) => !removed.has(oid)),
        ),
    };
};

export const getPreferenceChanges = (initial, current) => {
    const changes = [];
    if (current.global && current.global !== initial.global) {
        changes.push({ target: 'global', oid: current.global });
    }
    Object.entries(current.segments).forEach(([segmentId, oid]) => {
        if (initial.segments[segmentId] !== oid) {
            changes.push({ target: 'segment', segmentId, oid });
        }
    });
    Object.keys(initial.segments).forEach((segmentId) => {
        if (!(segmentId in current.segments)) {
            changes.push({ target: 'segment', segmentId, oid: null });
        }
    });
    return changes;
};

export const preferenceChangeLabel = (change) =>
    change.target === 'global' ? 'global default' : `segment ${change.segmentId}`;

// Changes that add, move or cancel dated windows, which the loaded windows do not show yet.
export const changesWindows = (changes) =>
    changes.some((change) => change.target === 'window' || change.start !== undefined);

// A change starting later than today does not alter the preferences in effect today.
const startsAfterToday = (change) =>
    Boolean(change.start) && dayjs(change.start).isAfter(dayjs(), 'day');

// Dates a segment change sends: effective_start only when it starts after today (the API
// starts it now otherwise), and the inclusive last day as the exclusive effective_end.
const windowDates = (change) => ({
    ...(startsAfterToday(change) ? { effective_start: windowStartFromDay(change.start) } : {}),
    ...(change.end ? { effective_end: windowEndFromLastDay(change.end) } : {}),
});

// A moved start of today goes out as now, since the API does not accept a start in the past.
const movedStart = (start) =>
    dayjs(start).isSame(dayjs(), 'day') ? dayjs().toISOString() : windowStartFromDay(start);

// Only the dates that changed are sent; a cleared end is sent as null to make it open-ended.
const movedDates = (change) => ({
    ...('start' in change ? { effective_start: movedStart(change.start) } : {}),
    ...('end' in change ? { effective_end: windowEndFromLastDay(change.end) } : {}),
});

const preferenceChangeRequests = {
    global: (therapyGroupName, change) =>
        put(therapyGroupGlobalPreferenceUrl(therapyGroupName), {
            product_service_oid: change.oid,
        }),
    replaceSegment: (therapyGroupName, change) =>
        put(therapyGroupSegmentPreferenceUrl(therapyGroupName, change.segmentId), {
            product_service_oid: change.oid,
            ...windowDates(change),
        }),
    endSegment: (therapyGroupName, change) =>
        httpDelete(therapyGroupSegmentPreferenceUrl(therapyGroupName, change.segmentId)),
    moveWindow: (_therapyGroupName, change) =>
        patch(preferenceWindowUrl(change.preferenceId), movedDates(change)),
    cancelWindow: (_therapyGroupName, change) =>
        httpDelete(preferenceWindowUrl(change.preferenceId)),
};

const changeKind = (change) => {
    if (change.target === 'global') {
        return 'global';
    }
    if (change.target === 'window') {
        return change.cancel ? 'cancelWindow' : 'moveWindow';
    }
    return change.oid ? 'replaceSegment' : 'endSegment';
};

export const preferenceChangeRequest = (therapyGroupName, change) =>
    preferenceChangeRequests[changeKind(change)](therapyGroupName, change);

// Moving or cancelling a window leaves today's preferences alone, except a scheduled window
// moved to start today, which takes effect now.
const appliesTodayAfterMove = (change) =>
    !change.cancel && 'start' in change && !startsAfterToday(change);

export const applyPreferenceChange = (preferences, change) => {
    if (startsAfterToday(change)) {
        return preferences;
    }
    if (change.target === 'window' && !appliesTodayAfterMove(change)) {
        return preferences;
    }
    if (change.target === 'global') {
        return { ...preferences, global: change.oid };
    }
    const segments = { ...preferences.segments };
    if (change.oid) {
        segments[change.segmentId] = change.oid;
    } else {
        delete segments[change.segmentId];
    }
    return { ...preferences, segments };
};
